import { useCallback, useReducer } from "react";
import { IOption } from "@evergis/ui";
import { TemplateType } from "../components/CalculationTemplates/TemplateForm/TemplateFormActions";

export type TemplateFormState = {
  attrValue: {
    option?: IOption;
    action?: IOption;
    value?: number;
  };
  attrAttr: {
    option1?: IOption;
    option2?: IOption;
    action?: IOption;
  };
  geometry: {
    option?: IOption;
  };
  attribute: {
    option?: IOption;
  };
};

type TemplateFormPayload = TemplateFormState[TemplateType];

const initialState = {
  attrValue: {},
  attrAttr: {},
  geometry: {},
  attribute: {},
};

function reducer(state: TemplateFormState, payload: Record<string, TemplateFormPayload>) {
  return { ...state, ...payload };
}

function getAttrValueResult(formState: TemplateFormState[TemplateType.AttrValue]) {
  return `${formState.option.value} ${formState.action.text} ${formState.value}`;
}

function getAttrAttrResult(formState: TemplateFormState[TemplateType.AttrAttr]) {
  return `${formState.option1.value} ${formState.action.text} ${formState.option2.value}`;
}

function getGeometryResult(formState: TemplateFormState[TemplateType.Geometry]) {
  return `${formState.option.value}`;
}

function getAttributeResult(formState: TemplateFormState[TemplateType.Geometry]) {
  return `${formState.option.value}`;
}

function buildResultTemplate(type: TemplateType, formState: TemplateFormState): string {
  switch (type) {
    case TemplateType.AttrValue:
      return getAttrValueResult(formState[type]);
    case TemplateType.AttrAttr:
      return getAttrAttrResult(formState[type]);
    case TemplateType.Geometry:
      return getGeometryResult(formState[type]);
    case TemplateType.Attribute:
      return getAttributeResult(formState[type]);
    default:
      return "";
  }
}

export const useTemplateForm = (
  templateType: TemplateType,
  handleSetTemplateValue: (value: string) => void,
  toggleTemplate: (type: string) => void,
) => {
  const [templateFormState, dispatch] = useReducer(reducer, initialState);

  const onChangeValue = useCallback(
    (value: number) => {
      dispatch({ attrValue: { ...templateFormState.attrValue, value } });
    },
    [dispatch, templateType, templateFormState],
  );

  const onChangeAttribute = useCallback(
    (parameterName: string) => (option: IOption[]) => {
      dispatch({ [templateType]: { ...templateFormState[templateType], [parameterName]: option[0] } });
    },
    [dispatch, templateType, templateFormState],
  );

  const applyTemplateValue = useCallback(() => {
    const result = buildResultTemplate(templateType, templateFormState);

    handleSetTemplateValue(result);
    toggleTemplate(templateType);
  }, [handleSetTemplateValue, templateType, templateFormState]);

  const clearForm = useCallback(() => {
    dispatch({ [templateType]: {} });
    toggleTemplate(templateType);
  }, [dispatch, templateType, templateFormState]);

  return { templateFormState, onChangeValue, onChangeAttribute, applyTemplateValue, clearForm };
};
